import { useEffect, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ImagePlus, Trash2 } from "lucide-react";
import client, { absoluteUrl } from "../api/client";
import { GSTIN, STATE_OPTIONS } from "./gst";
import "../Moodboard/Moodboard.css";
import "../Architect/Architect.css";
import "./Quotations.css";

const PAN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const IFSC = /^[A-Z]{4}0[A-Z0-9]{6}$/;

function ImageField({ label, hint, url, busy, onUpload, onClear }) {
  const input = useRef(null);
  return (
    <div className="ar-field">
      <span className="ar-field-label">{label}</span>
      <div className="ar-logo-row">
        <div className="ar-logo-box">{url ? <img src={absoluteUrl(url) || undefined} alt={label} /> : <span>None</span>}</div>
        <div className="ar-logo-actions">
          <button type="button" className="mb-wide-button ar-fit" onClick={() => input.current?.click()} disabled={busy}>
            <ImagePlus size={15} strokeWidth={1.8} aria-hidden="true" />
            {busy ? "Uploading..." : url ? "Change" : "Upload"}
          </button>
          {url && (
            <button type="button" className="mb-text-button" onClick={onClear}>
              <Trash2 size={14} strokeWidth={1.8} aria-hidden="true" /> Remove
            </button>
          )}
          <p className="ar-muted ar-small">{hint}</p>
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            onChange={(event) => {
              onUpload(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default function CompanySettings() {
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    client
      .get("/api/admin/company")
      .then(({ data }) => {
        setForm(data);
        setSaved(JSON.stringify(data));
      })
      .catch(() => setError("Could not load the company details."));
  }, []);

  useEffect(() => {
    const warn = (event) => {
      if (form && saved && JSON.stringify(form) !== saved) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [form, saved]);

  if (!form) {
    return (
      <main className="mb-page ar-page">
        {error ? <p className="mb-error">{error}</p> : <p className="mb-muted">Loading...</p>}
      </main>
    );
  }

  const set = (key, transform = (value) => value) => (event) => setForm((current) => ({ ...current, [key]: transform(event.target.value) }));
  const upper = (value) => value.toUpperCase().replace(/\s/g, "");
  const gstin = (form.gstin || "").trim();
  const problems = {
    gstin: gstin && !GSTIN.test(gstin) ? "15 characters, like 27ABCDE1234F1Z5" : GSTIN.test(gstin) && form.state_code && gstin.slice(0, 2) !== form.state_code ? `The GSTIN belongs to state ${gstin.slice(0, 2)}` : "",
    pan: form.pan && !PAN.test(form.pan) ? "Like ABCDE1234F" : "",
    bank_ifsc: form.bank_ifsc && !IFSC.test(form.bank_ifsc) ? "Like HDFC0001234" : "",
  };
  const blocked = Object.values(problems).some(Boolean);
  const dirty = JSON.stringify(form) !== saved;

  const upload = (kind) => async (file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await client.post("/api/uploads", body);
      setForm((current) => ({ ...current, [`${kind}_upload_id`]: data.id, [`${kind}_url`]: data.url }));
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not upload the image.");
    } finally {
      setUploading("");
    }
  };

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data } = await client.put("/api/admin/company", form);
      setForm(data);
      setSaved(JSON.stringify(data));
      toast.success("Company details saved.");
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, { limit = 160, transform, placeholder, type = "text" } = {}) => (
    <label className={`ar-field${problems[key] ? " is-invalid" : ""}`} key={key}>
      <span className="ar-field-label">{label}</span>
      <input type={type} value={form[key] ?? ""} maxLength={limit} onChange={set(key, transform)} placeholder={placeholder} />
      {problems[key] && <span className="ar-field-error">{problems[key]}</span>}
    </label>
  );

  return (
    <main className="mb-page ar-page">
      <ToastContainer position="bottom-right" />
      <header className="mb-page-head">
        <div>
          <p className="mb-kicker">Administration</p>
          <h1>Company details</h1>
        </div>
      </header>
      <p className="ar-muted ar-intro">
        These appear on every quotation. A quotation keeps the details it was sent with, so changes here only affect drafts
        and new quotations.
      </p>
      <form className="qt-settings" onSubmit={save}>
        <section className="ar-card">
          <h2>Company</h2>
          <div className="ar-grid-2">
            {field("company_name", "Company name")}
            <label className="ar-field">
              <span className="ar-field-label">State</span>
              <select value={form.state_code || ""} onChange={set("state_code")}>
                <option value="">Choose a state</option>
                {STATE_OPTIONS.map(([code, name]) => (
                  <option key={code} value={code}>
                    {name} ({code})
                  </option>
                ))}
              </select>
            </label>
            {field("gstin", "GSTIN", { limit: 15, transform: upper, placeholder: "27ABCDE1234F1Z5" })}
            {field("pan", "PAN", { limit: 10, transform: upper, placeholder: "ABCDE1234F" })}
            {field("phone", "Phone", { limit: 40 })}
            {field("email", "Email (replies to quotations go here)", { type: "email" })}
            {field("website", "Website")}
          </div>
          <label className="ar-field">
            <span className="ar-field-label">Address</span>
            <textarea rows={2} value={form.address || ""} maxLength={500} onChange={set("address")} />
          </label>
        </section>

        <section className="ar-card">
          <h2>Bank details</h2>
          <div className="ar-grid-2">
            {field("bank_name", "Bank", { limit: 120 })}
            {field("bank_account_name", "Account name")}
            {field("bank_account_no", "Account number", { limit: 40 })}
            {field("bank_ifsc", "IFSC", { limit: 11, transform: upper, placeholder: "HDFC0001234" })}
            {field("bank_branch", "Branch")}
            {field("upi_id", "UPI ID", { limit: 80, placeholder: "name@bank" })}
          </div>
        </section>

        <section className="ar-card">
          <h2>Logo and signature</h2>
          <div className="ar-grid-2">
            <ImageField
              label="Logo"
              hint="Shown at the top of quotations. Without one, the JUMIG Store logo is used."
              url={form.logo_url}
              busy={uploading === "logo"}
              onUpload={upload("logo")}
              onClear={() => setForm((current) => ({ ...current, logo_upload_id: null, logo_url: null }))}
            />
            <ImageField
              label="Signature"
              hint="A PNG with a transparent background, shown above 'Authorised signatory'."
              url={form.signature_url}
              busy={uploading === "signature"}
              onUpload={upload("signature")}
              onClear={() => setForm((current) => ({ ...current, signature_upload_id: null, signature_url: null }))}
            />
          </div>
        </section>

        <section className="ar-card">
          <h2>Quotation defaults</h2>
          <div className="ar-grid-2">
            {field("quote_prefix", "Number prefix", { limit: 20, placeholder: "JS/Q" })}
            <label className="ar-field">
              <span className="ar-field-label">Valid for (days)</span>
              <input type="number" min={1} max={365} value={form.validity_days ?? 15} onChange={set("validity_days")} />
            </label>
          </div>
          <p className="ar-muted ar-small">
            Numbers look like {form.quote_prefix || "JS/Q"}/2026-27/0001 and start again at 0001 each financial year (April to March).
          </p>
          <label className="ar-field">
            <span className="ar-field-label">Default terms and conditions</span>
            <textarea rows={6} value={form.default_terms || ""} maxLength={5000} onChange={set("default_terms")} />
          </label>
          <label className="ar-field">
            <span className="ar-field-label">Default notes</span>
            <textarea rows={2} value={form.default_notes || ""} maxLength={5000} onChange={set("default_notes")} />
          </label>
        </section>

        <div className="qt-save-bar">
          <span className="ar-muted ar-small">{dirty ? "Unsaved changes" : "All changes saved"}</span>
          <button type="submit" className="mb-primary" disabled={busy || blocked || !dirty}>
            {busy ? "Saving..." : "Save company details"}
          </button>
        </div>
      </form>
    </main>
  );
}
